import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'styled-components';
import {
  BarChart, Bar, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  buildLabelFormatter, buildXTickFormatter, yTickFormatter,
} from './utils';
import { LwColumnChartStyles } from '../styles';
import buildTooltipContent from './hoc-tooltip-content';

const Container = styled.div`
  width: 100%;
  height: 260px;

  & svg {
    pointer-events: all;
    ${LwColumnChartStyles}
  }
`;

export default function ColumnChart({
  type, bucket, granularity, tooltipValueContent,
  // Injections to make this testable
  IContainer, IBarChart, IYAxis, IBar, ICell,
}) {
  const [active, setActive] = React.useState(null);

  const onEnterBar = data => setActive(data.time);
  const onLeaveBar = () => setActive(null);

  return (
    <Container>
      <IContainer>
        <IBarChart
          data={bucket}
          margin={{
            top: 32, right: 0, left: 0, bottom: 20,
          }}
        >
          <CartesianGrid vertical={false} horizontal />
          <IYAxis
            dataKey="value"
            interval="preserveStartEnd"
            tickCount={4}
            tickSize={40}
            axisLine={false}
            tickMargin={-30}
            tickFormatter={yTickFormatter}
          />
          <XAxis
            dataKey="time"
            tickSize={20}
            height={1}
            stroke={null}
            minTickGap={164}
            tickFormatter={buildXTickFormatter(granularity)}
          />
          <IBar dataKey="value" name={type} onMouseEnter={onEnterBar} onMouseLeave={onLeaveBar}>
            {bucket.map(data => (
              <ICell key={`bar-cell-${data.time}`} className={classNames({ active: data.time === active })} />
            ))}
          </IBar>
          <Tooltip
            offset={10}
            isAnimationActive={false}
            labelFormatter={buildLabelFormatter(granularity)}
            content={buildTooltipContent({ valueContent: tooltipValueContent })}
            cursor={false}
          />
        </IBarChart>
      </IContainer>
    </Container>
  );
}

ColumnChart.propTypes = {
  type: PropTypes.string,
  bucket: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number.isRequired,
    time: PropTypes.string.isRequired,
  })).isRequired,
  granularity: PropTypes.oneOf(['d', 'w', 'm']).isRequired,
  tooltipValueContent: PropTypes.elementType.isRequired,
  IContainer: PropTypes.elementType,
  IBarChart: PropTypes.elementType,
  IYAxis: PropTypes.elementType,
  IBar: PropTypes.elementType,
  ICell: PropTypes.elementType,
};

ColumnChart.defaultProps = {
  type: '',
  IContainer: ResponsiveContainer,
  IBarChart: BarChart,
  IYAxis: YAxis,
  IBar: Bar,
  ICell: Cell,
};
